// Practices grid. Legacy: queues "/practice <id>" for the chat. Engine v2
// users launch through the practice-button API (spec §3.6): catalog from
// GET /engine/skills, tap -> POST /engine/launch (clinical rails inside the
// engine). Any catalog error keeps the legacy list and flow untouched.
//
// #4483: while the catalog request is in flight the screen shows the
// loading placeholder, never the legacy grid — the legacy cards flashing
// in for a second before the engine list swapped them was the bug. The
// fetched catalog is cached for the session so re-entering the section
// doesn't re-flash either.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PRACTICES, legacyMetaFor } from './practices.data'
import { getCatalogCache, setCatalogCache } from './catalogCache'
import { chatApi } from '../../api/chat'
import { useChatStore } from '../../store/chatStore'
import { useMessagingStore } from '../../store/messagingStore'
import { useProfileStore } from '../../store/profileStore'
import { useToastStore } from '../../store/toastStore'
import * as metrica from '../../lib/metrica'
import type { EngineSkill } from '../../types/api'
import styles from './Practices.module.css'

// Registry kind -> card tag line (display only; rails gate at launch).
const KIND_TAG: Record<string, string> = {
  micro_practice: 'микропрактика',
  service: 'сервис',
}

type CatalogState = 'loading' | 'engine' | 'legacy'

export default function Practices() {
  const navigate = useNavigate()
  const queueInput = useChatStore((s: ReturnType<typeof useChatStore.getState>) => s.queueInput)
  const stats = useProfileStore((s) => s.stats)
  const loadStats = useProfileStore((s) => s.loadStats)
  const toast = useToastStore((s) => s.push)

  const [engineSkills, setEngineSkills] = useState<EngineSkill[] | null>(getCatalogCache())
  const [catalogState, setCatalogState] = useState<CatalogState>(
    getCatalogCache() ? 'engine' : 'loading',
  )
  const [launching, setLaunching] = useState<string | null>(null)

  useEffect(() => {
    loadStats()
  }, [loadStats])

  // Engine catalog: cached hit renders instantly; a fresh fetch keeps the
  // placeholder until it settles — success switches to the engine grid,
  // any error (409 = not engine-enabled, network, auth) falls back to the
  // legacy list.
  useEffect(() => {
    if (getCatalogCache()) return
    let cancelled = false
    chatApi
      .engineSkills()
      .then((res) => {
        if (cancelled) return
        if (res.skills?.length) {
          setCatalogCache(res.skills)
          setEngineSkills(res.skills)
          setCatalogState('engine')
        } else {
          setCatalogState('legacy')
        }
      })
      .catch(() => {
        if (!cancelled) setCatalogState('legacy')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Soft-launch gate: practices require a subscription (original _showSectionPaywall).
  // While stats are unknown we render a neutral placeholder, NOT the grid:
  // failing open flashed the full practice list for a second on refresh
  // before the paywall swapped in (Redmine #4477).
  const locked = stats != null && !stats.subscription_status

  // Fire the paywall-shown goal once (original sidebar.js:112).
  useEffect(() => {
    if (locked) metrica.reachGoal('section_paywall', { section: 'practices' })
  }, [locked])

  const startLegacy = (practice: (typeof PRACTICES)[number]) => {
    metrica.reachGoal('practice_start', { id: practice.id })
    queueInput(`/practice ${practice.id}`, `Начинаем практику: ${practice.name}`)
    navigate('/')
  }

  const startEngine = async (skill: EngineSkill) => {
    if (launching) return
    setLaunching(skill.id)
    metrica.reachGoal('practice_start', { id: skill.id })
    const { addMessage, setTyping, setLoading } = useMessagingStore.getState()
    addMessage('user', `Начинаем практику: ${skill.name}`)
    setTyping(true)
    setLoading(true)
    try {
      const reply = await chatApi.engineLaunch(skill.id)
      setTyping(false)
      addMessage('assistant', reply.content, {
        contentType: reply.content_type || 'text',
        mediaUrl: reply.media_url || null,
        metadata: reply.metadata || null,
      })
      navigate('/')
    } catch (err) {
      setTyping(false)
      toast((err instanceof Error && err.message) || 'Не удалось запустить практику', 'error')
    } finally {
      setLoading(false)
      setLaunching(null)
    }
  }

  // Subscription status still loading — show nothing gated either way.
  if (stats == null) {
    return (
      <div className={styles.locked} aria-busy="true">
        <p className={styles.loadingText}>Загрузка…</p>
      </div>
    )
  }

  // Locked (#4477): the paywall centers in the section viewport — the teaser
  // catalog behind it is absolutely positioned and clipped, so it is never
  // the box that places the card (redmine 4463, 26.10 item 1: «закрыть
  // блюром — чтобы было видно, что там может быть»).
  if (locked) {
    return (
      <div className={styles.lockedPreview}>
        <div className={styles.lockedGrid} aria-hidden="true">
          {PRACTICES.map((p) => (
            <div key={p.id} className={styles.card}>
              <div className={styles.text}>
                <h2 className={styles.name}>{p.name}</h2>
                <p className={styles.desc}>{p.desc}</p>
                <span className={styles.tag}>{p.tag}</span>
              </div>
              <img src={p.img} alt="" className={styles.img} />
            </div>
          ))}
        </div>
        <div className={styles.lockedOverlay}>
          <div className={styles.paywallCard}>
            <div className={styles.paywallIcon}>🔒</div>
            <h3>Раздел с практиками пока недоступен</h3>
            <p>Он откроется в полной версии</p>
            <button className={styles.paywallBtn} onClick={() => navigate('/')}>Вернуться к диалогу</button>
          </div>
        </div>
      </div>
    )
  }

  // Catalog still loading (#4483): the placeholder, never the legacy grid —
  // the legacy cards are the fallback for a FAILED fetch, not a spinner.
  if (catalogState === 'loading') {
    return (
      <div className={styles.locked} aria-busy="true">
        <p className={styles.loadingText}>Загрузка…</p>
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <h1 className={styles.title}>Практики</h1>

      <div className={styles.grid}>
        {catalogState === 'engine' && engineSkills
          ? engineSkills.map((s) => {
              const meta = legacyMetaFor(s.name)
              return (
                <button
                  key={s.id}
                  className={styles.card}
                  disabled={launching === s.id}
                  onClick={() => startEngine(s)}
                >
                  <div className={styles.text}>
                    <h2 className={styles.name}>
                      {launching === s.id ? 'Запускаем…' : s.name} <span className={styles.arrow}>›</span>
                    </h2>
                    {meta && <p className={styles.desc}>{meta.desc}</p>}
                    <span className={styles.tag}>{meta?.tag ?? (KIND_TAG[s.kind] || s.kind)}</span>
                  </div>
                  {meta && <img src={meta.img} alt="" className={styles.img} />}
                </button>
              )
            })
          : PRACTICES.map((p) => (
              <button key={p.id} className={styles.card} onClick={() => startLegacy(p)}>
                <div className={styles.text}>
                  <h2 className={styles.name}>
                    {p.name} <span className={styles.arrow}>›</span>
                  </h2>
                  <p className={styles.desc}>{p.desc}</p>
                  <span className={styles.tag}>{p.tag}</span>
                </div>
                <img src={p.img} alt="" className={styles.img} />
              </button>
            ))}
      </div>
    </div>
  )
}
